import moment from 'moment';
import 'moment-timezone';

/** The last-minute window: shared by the enhanced recommended rate and last-minute Auto-Confirm. */
export const LAST_MINUTE_WINDOW_HOURS = 48;

/**
 * Combines a shift date with a `"h:mm am/pm"` arrival time into a single start `Date`.
 * Mirrors the parsing used on the Review step; returns the date unchanged when the time
 * string does not match, so callers get a defined (never NaN) Date.
 */
export const parseShiftStart = (date: Date, arrivalTime: string): Date => {
  const result = new Date(date);
  const match = arrivalTime.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);

  if (!match) return result;

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3].toLowerCase();

  if (period === 'pm' && hours !== 12) hours += 12;
  if (period === 'am' && hours === 12) hours = 0;

  result.setHours(hours, minutes, 0, 0);
  return result;
};

/**
 * True when the request is for exactly one shift (SDB) whose start is still in the future and
 * less than 48 hours away — the condition for defaulting the enhanced recommended rate.
 *
 * Multi-shift (MDB) requests, no dates, or a start 48h+ away all return false.
 *
 * NOTE: resolves the arrival time in the BROWSER's timezone, so an office in another zone is
 * judged against the wrong clock near the window edge. `isShiftWithinLastMinuteWindow` below is
 * the corrected form. Switching this one over changes which requests get the enhanced rate, so
 * it needs its own ticket rather than riding along with Auto-Confirm.
 */
export const isSingleShiftWithin48h = (
  dates: Date[] | undefined,
  arrivalTime: string | undefined,
  now: Date = new Date(),
): boolean => {
  if (!dates || dates.length !== 1 || !arrivalTime) return false;

  const shiftStart = parseShiftStart(dates[0], arrivalTime);
  const hoursUntilShift = (shiftStart.getTime() - now.getTime()) / (1000 * 60 * 60);

  return hoursUntilShift > 0 && hoursUntilShift < LAST_MINUTE_WINDOW_HOURS;
};

/**
 * True when a shift starts less than 48h from now, judged in the OFFICE's timezone. A start that
 * has already passed today counts too — see the note on the return.
 *
 * The arrival time is a wall clock the backend resolves against the office (`buildJobPayloads`
 * sends it as `localStart`), so reading it in the browser's timezone shifts the shift by the
 * offset between the two — enough to drop an imminent shift out of the window.
 */
export const isShiftWithinLastMinuteWindow = (
  date: Date,
  arrivalTime: string,
  timeZone: string | undefined,
): boolean => {
  // Strict parsing: moment otherwise reads an unrecognised time as midnight of that day, which
  // without a lower bound below would read as "already started" and wrongly lock the toggles.
  const shiftStart = moment.tz(
    `${moment(date).format('YYYY-MM-DD')} ${arrivalTime}`,
    'YYYY-MM-DD hh:mm a',
    true,
    timeZone || moment.tz.guess(),
  );
  if (!shiftStart.isValid()) return false;

  // No lower bound: a shift posted for today whose arrival time has already passed is the most
  // last-minute case there is, and an office cannot pick a date before today. Bounding this at
  // zero left those shifts unlocked (GOTU-5399, raised in QA by Waweru).
  return shiftStart.diff(moment(), 'hours', true) < LAST_MINUTE_WINDOW_HOURS;
};
